import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";

const Index = lazy(() => import("@/pages/Index"));
const Contact = lazy(() => import("@/pages/Contact"));
const Prospecting = lazy(() => import("@/pages/Prospecting"));
const About = lazy(() => import("@/pages/About"));
const Weeks = lazy(() => import("@/pages/Weeks"));
const Stages = lazy(() => import("@/pages/Stages"));
const TradeShows = lazy(() => import("@/pages/TradeShows"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const Plans = lazy(() => import("@/pages/Plans"));
const Profile = lazy(() => import("@/pages/Profile"));
const Reports = lazy(() => import("@/pages/Reports"));
const RocketAnimation = lazy(() => import("@/pages/RocketAnimation"));
const SalesGuide = lazy(() => import("@/pages/SalesGuide"));
const Success = lazy(() => import("@/pages/Success"));
const RegistrationComplete = lazy(() => import("@/pages/RegistrationComplete"));
const Credentials = lazy(() => import("@/pages/Credentials"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Corporation = lazy(() => import("@/pages/Corporation"));
const Communicator = lazy(() => import("@/pages/Communicator"));
const OutlookReturn = lazy(() => import("@/pages/oauth/OutlookReturn"));
const OAuthConsent = lazy(() => import("@/pages/oauth/Consent"));
const ManageSubscription = lazy(() => import("@/pages/ManageSubscription"));
const Planning = lazy(() => import("@/pages/Planning"));
const Mapping = lazy(() => import("@/pages/Mapping"));
import { RegistrationProvider } from "@/components/auth/RegistrationContext";
import { AuthSessionProvider } from "@/hooks/useAuthSession";
import { Toaster } from "@/components/ui/toaster";
import InstallAppBanner from "@/components/InstallAppBanner";
const FeaturePlanning = lazy(() => import("@/pages/features").then((m) => ({ default: m.Planning })));
const FeatureMapping = lazy(() => import("@/pages/features").then((m) => ({ default: m.Mapping })));
const FeatureFollowUps = lazy(() => import("@/pages/features").then((m) => ({ default: m.FollowUps })));
const FeaturePipeline = lazy(() => import("@/pages/features").then((m) => ({ default: m.Pipeline })));
const FeatureTradeShows = lazy(() => import("@/pages/features").then((m) => ({ default: m.TradeShows })));
const FeatureProspecting = lazy(() => import("@/pages/features").then((m) => ({ default: m.Prospecting })));
const FeatureContactsImport = lazy(() => import("@/pages/features").then((m) => ({ default: m.ContactsImport })));
const FeatureWeeklyLog = lazy(() => import("@/pages/features").then((m) => ({ default: m.WeeklyLog })));
const FeatureReports = lazy(() => import("@/pages/features").then((m) => ({ default: m.Reports })));
const FeatureAISalesAssistant = lazy(() => import("@/pages/features").then((m) => ({ default: m.AISalesAssistant })));



function App() {
  return (
    <Router>
      <AuthSessionProvider>
        <RegistrationProvider>
        <Toaster />
        <Analytics />
        <Suspense fallback={<div className="min-h-screen" />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/sales-guide" element={<SalesGuide />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/success" element={<Success />} />
          <Route path="/registration-complete" element={<RegistrationComplete />} />
          <Route path="/rocket" element={<RocketAnimation />} />
          <Route path="/credentials" element={<Credentials />} />
          <Route path="/oauth/outlook/return" element={<OutlookReturn />} />
          <Route path="/oauth/consent" element={<OAuthConsent />} />


          {/* Public marketing feature pages (SEO) */}
          <Route path="/features/planning" element={<FeaturePlanning />} />
          <Route path="/features/mapping" element={<FeatureMapping />} />
          <Route path="/features/follow-ups" element={<FeatureFollowUps />} />
          <Route path="/features/pipeline" element={<FeaturePipeline />} />
          <Route path="/features/trade-shows" element={<FeatureTradeShows />} />
          <Route path="/features/prospecting" element={<FeatureProspecting />} />
          <Route path="/features/contacts-import" element={<FeatureContactsImport />} />
          <Route path="/features/weekly-log" element={<FeatureWeeklyLog />} />
          <Route path="/features/reports" element={<FeatureReports />} />
          <Route path="/features/ai-sales-assistant" element={<FeatureAISalesAssistant />} />

          <Route path="/index" element={<ProtectedRoute><Index /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/prospecting" element={<ProtectedRoute><Prospecting /></ProtectedRoute>} />
          <Route path="/weeks" element={<ProtectedRoute><Weeks /></ProtectedRoute>} />
          <Route path="/stages" element={<ProtectedRoute><Stages /></ProtectedRoute>} />
          <Route path="/trade-shows" element={<ProtectedRoute><TradeShows /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/calendar" element={<Navigate to="/index" replace />} />
          <Route path="/planning" element={<ProtectedRoute><Planning /></ProtectedRoute>} />
          <Route path="/mapping" element={<ProtectedRoute><Mapping /></ProtectedRoute>} />
          <Route path="/corporation" element={<ProtectedRoute><Corporation /></ProtectedRoute>} />
          <Route path="/communicator" element={<ProtectedRoute><Communicator /></ProtectedRoute>} />
          <Route path="/manage-subscription" element={<ProtectedRoute><ManageSubscription /></ProtectedRoute>} />
        </Routes>
        </Suspense>
        <InstallAppBanner />
        </RegistrationProvider>
      </AuthSessionProvider>
    </Router>
  );
}

export default App;
